<div class="space-y-6">
  <app-page-header
    [backTo]="['/operators']"
    backLabel="Retour aux opérateurs"
  >
    @if (op(); as o) {
      <div slot="actions" class="flex flex-wrap items-center gap-2">
        @if (canApprove()) {
          <button
            type="button"
            (click)="openApprove()"
            class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600"
          >
            Approuver
          </button>
        }
        @if (!o.isSystemOwned) {
          <a
            [routerLink]="['/operators', o.id, 'edit']"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Modifier
          </a>
        }
        <button
          type="button"
          (click)="openCredit()"
          class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
        >
          Référence CREDIT
        </button>
      </div>
    }
  </app-page-header>

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      loadingMessage="Chargement…"
      (retry)="fetch()"
    />
  } @else if (op(); as o) {
    <!-- Header card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div class="flex flex-wrap items-center gap-3">
            <h1 class="text-xl font-semibold text-gray-800 dark:text-white/90">
              {{ o.businessName || ('Opérateur #' + o.id) }}
            </h1>
            <span
              class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              [class.bg-brand-50]="statusMeta[o.operatorStatus].palette === 'brand'"
              [class.text-brand-700]="statusMeta[o.operatorStatus].palette === 'brand'"
              [class.dark:bg-brand-500/15]="statusMeta[o.operatorStatus].palette === 'brand'"
              [class.dark:text-brand-400]="statusMeta[o.operatorStatus].palette === 'brand'"
              [class.bg-warning-50]="statusMeta[o.operatorStatus].palette === 'warning'"
              [class.text-warning-700]="statusMeta[o.operatorStatus].palette === 'warning'"
              [class.bg-gray-100]="statusMeta[o.operatorStatus].palette === 'gray'"
              [class.text-gray-700]="statusMeta[o.operatorStatus].palette === 'gray'"
              [class.bg-error-50]="statusMeta[o.operatorStatus].palette === 'error'"
              [class.text-error-700]="statusMeta[o.operatorStatus].palette === 'error'"
            >
              {{ statusMeta[o.operatorStatus].label }}
            </span>
            @if (o.isFallback) {
              <span class="inline-flex items-center rounded-full bg-blue-light-50 px-2 py-0.5 text-xs font-medium text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400">Fallback plateforme</span>
            } @else if (o.isSystemOwned) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">Système</span>
            }
            @if (o.licenseTier) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                Licence {{ tierLabel[o.licenseTier] }}
              </span>
            }
          </div>
          @if (!o.isFallback) {
            <div class="mt-4">
              <app-user-cell [user]="o.user" [fallbackId]="o.userId" size="md" />
            </div>
          }
          <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Opérateur #{{ o.id }}
            @if (o.zoneCode) {
              <span class="mx-2 text-gray-300 dark:text-gray-600">·</span>
              Zone <span class="font-mono">{{ o.zoneCode }}</span>
            }
          </p>
        </div>
        @if (walletLink(); as wl) {
          <a
            [routerLink]="wl"
            class="inline-flex h-fit items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Voir le wallet →
          </a>
        }
      </div>
    </section>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Identification -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Identification</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Nom commercial</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ o.businessName || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Téléphone</dt>
            <dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ o.phone || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Email</dt>
            <dd class="text-right text-gray-800 dark:text-white/90 break-all">{{ o.email || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Zone</dt>
            <dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ o.zoneCode || '—' }}</dd>
          </div>
        </dl>
      </section>

      <!-- Commission rates -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Taux & licence</h2>
            @if (effectiveRates(); as er) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Taux effectifs appliqués —
                @if (er.usesCustomRates) {
                  <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
                    Personnalisé
                  </span>
                } @else {
                  <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    Global
                  </span>
                }
              </p>
            }
          </div>
          @if (!o.isSystemOwned) {
            <button
              type="button"
              (click)="openCommission()"
              class="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Modifier
            </button>
          }
        </div>

        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Palier de licence</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ o.licenseTier ? tierLabel[o.licenseTier] : '—' }}</dd>
          </div>

          @if (effectiveRates(); as er) {
            <!-- Bloc taux effectifs (vue claire) -->
            <div class="rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <div class="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Total</p>
                  <p class="mt-1 text-base font-semibold text-gray-800 dark:text-white/90">{{ er.totalPct }} %</p>
                </div>
                <div>
                  <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Plateforme</p>
                  <p class="mt-1 text-base font-semibold text-gray-800 dark:text-white/90">{{ er.platformPct }} %</p>
                </div>
                <div>
                  <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Opérateur</p>
                  <p class="mt-1 text-base font-semibold text-gray-800 dark:text-white/90">{{ er.operatorPct }} %</p>
                </div>
              </div>
              @if (er.usesCustomRates && globalConfig(); as g) {
                <p class="mt-3 border-t border-gray-200 pt-2 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  Défaut global : {{ g.defaultTotalPct }} % (plat. {{ g.defaultPlatformPct }} % / opé. {{ g.defaultOperatorPct }} %)
                </p>
              }
            </div>
          }

          <div class="flex justify-between gap-4 border-t border-gray-100 pt-3 dark:border-gray-800">
            <dt class="text-gray-500 dark:text-gray-400">Référence CREDIT individuelle</dt>
            <dd class="text-right">
              @if (o.creditReferenceAmount != null) {
                <span class="text-gray-800 dark:text-white/90">{{ o.creditReferenceAmount | number:'1.0-0' }} <span class="text-xs text-gray-400">XOF</span></span>
              } @else {
                <span class="text-xs text-gray-400">défaut global</span>
              }
            </dd>
          </div>
          @if (o.creditThresholdState != null) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Dernier palier franchi</dt>
              <dd class="text-right text-gray-800 dark:text-white/90">{{ o.creditThresholdState }} %</dd>
            </div>
          }
        </dl>
      </section>
    </div>

    <!-- Justificatifs KYC (#66) — le fallback système n'a pas de dossier. -->
    @if (!o.isFallback) {
      <section id="kyc" class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <div>
            <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Justificatifs KYC</h2>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Valider un document arme son suivi d'expiration (rappels J-30 / J-15 / J-7).
            </p>
          </div>
          @if (o.kycStatus; as ks) {
            <div class="flex items-center gap-4">
              <!-- Statuer ici : les pièces sont sous les yeux, pas d'aller-retour. -->
              @if (dossierDecidable()) {
                <div class="flex items-center gap-1.5">
                  <button type="button" (click)="openDossierReject()" [disabled]="dossierApproving()"
                    class="rounded-md px-2.5 py-1 text-xs font-medium text-error-600 transition hover:bg-error-50 disabled:opacity-40 dark:text-error-400 dark:hover:bg-error-500/10">
                    Rejeter le dossier
                  </button>
                  <button type="button" (click)="approveDossier()" [disabled]="dossierApproving()"
                    class="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
                    @if (dossierApproving()) {
                      <svg class="size-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
                      Validation…
                    } @else { Valider le dossier }
                  </button>
                </div>
              }
              <div class="text-right">
              <p class="text-xs text-gray-500 dark:text-gray-400">Dossier</p>
              <span
                class="mt-0.5 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                [class.bg-brand-50]="dossierMeta[ks].palette === 'brand'"
                [class.text-brand-700]="dossierMeta[ks].palette === 'brand'"
                [class.dark:bg-brand-500/15]="dossierMeta[ks].palette === 'brand'"
                [class.dark:text-brand-400]="dossierMeta[ks].palette === 'brand'"
                [class.bg-warning-50]="dossierMeta[ks].palette === 'warning'"
                [class.text-warning-700]="dossierMeta[ks].palette === 'warning'"
                [class.dark:bg-warning-500/15]="dossierMeta[ks].palette === 'warning'"
                [class.dark:text-orange-400]="dossierMeta[ks].palette === 'warning'"
                [class.bg-error-50]="dossierMeta[ks].palette === 'error'"
                [class.text-error-700]="dossierMeta[ks].palette === 'error'"
                [class.dark:bg-error-500/15]="dossierMeta[ks].palette === 'error'"
                [class.dark:text-error-400]="dossierMeta[ks].palette === 'error'"
                [class.bg-gray-100]="dossierMeta[ks].palette === 'gray'"
                [class.text-gray-700]="dossierMeta[ks].palette === 'gray'"
                [class.dark:bg-gray-800]="dossierMeta[ks].palette === 'gray'"
                [class.dark:text-gray-300]="dossierMeta[ks].palette === 'gray'"
              >{{ dossierMeta[ks].label }}</span>
              </div>
            </div>
          }
        </div>

        @if (dossierFlash()) {
          <div class="flex items-start justify-between gap-3 border-b border-gray-200 bg-brand-50 px-6 py-3 text-sm text-brand-800 dark:border-gray-800 dark:bg-brand-500/10 dark:text-brand-300">
            <span>{{ dossierFlash() }}</span>
            <button type="button" (click)="dossierFlash.set(null)" class="shrink-0 text-brand-700 hover:text-brand-900 dark:text-brand-400">✕</button>
          </div>
        }
        @if (dossierError()) {
          <div class="border-b border-gray-200 bg-error-50 px-6 py-3 text-sm text-error-700 dark:border-gray-800 dark:bg-error-500/10 dark:text-error-400">
            {{ dossierError() }}
          </div>
        }

        @if (o.kycStatus === 'UNDER_REVIEW') {
          <div class="border-b border-gray-200 bg-warning-50 px-6 py-3 text-sm text-warning-700 dark:border-gray-800 dark:bg-warning-500/10 dark:text-orange-400">
            Dossier soumis, en attente de décision — inspectez les pièces ci-dessous, puis statuez.
          </div>
        } @else if (o.kycStatus === 'REJECTED' && o.kycRejectionReason) {
          <div class="border-b border-gray-200 bg-error-50 px-6 py-3 text-sm text-error-700 dark:border-gray-800 dark:bg-error-500/10 dark:text-error-400">
            Dossier rejeté — motif : {{ o.kycRejectionReason }}
          </div>
        }

        @if (kycError() || kycLoading() || kycDocs().length === 0) {
          <app-data-state
            [loading]="kycLoading()"
            [error]="kycError()"
            [empty]="!kycLoading() && !kycError() && kycDocs().length === 0"
            loadingMessage="Chargement des justificatifs…"
            emptyMessage="Aucun justificatif téléversé par cet opérateur."
            (retry)="loadKycDocs()"
          />
        } @else {
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead class="bg-gray-50 dark:bg-white/[0.02]">
                <tr>
                  <th class="px-6 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Document</th>
                  <th class="px-6 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Statut</th>
                  <th class="px-6 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Émission</th>
                  <th class="px-6 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Expiration</th>
                  <th class="px-6 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-800">
                @for (doc of kycDocs(); track doc.id) {
                  <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                    <td class="px-6 py-4">
                      <div class="flex items-center gap-3">
                        @if (isImage(doc.documentUrl)) {
                          <button type="button" (click)="lightboxSrc.set(doc.documentUrl)" class="shrink-0" title="Agrandir">
                            <img [src]="doc.documentUrl" [alt]="docTypeLabel[doc.documentType]"
                              class="size-12 rounded-lg border border-gray-200 object-cover transition hover:opacity-80 dark:border-gray-700" />
                          </button>
                        }
                        <div>
                          <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ docTypeLabel[doc.documentType] }}</p>
                          <a [href]="doc.documentUrl" target="_blank" rel="noopener"
                            class="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400">Ouvrir le fichier →</a>
                          @if (doc.kycStatus === 'REJECTED' && doc.rejectionReason) {
                            <p class="mt-0.5 text-xs text-error-600 dark:text-error-400">Motif : {{ doc.rejectionReason }}</p>
                          }
                        </div>
                      </div>
                    </td>
                    <td class="whitespace-nowrap px-6 py-4">
                      <span
                        class="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                        [class.bg-brand-50]="docStatusMeta[doc.kycStatus].palette === 'brand'"
                        [class.text-brand-700]="docStatusMeta[doc.kycStatus].palette === 'brand'"
                        [class.dark:bg-brand-500/15]="docStatusMeta[doc.kycStatus].palette === 'brand'"
                        [class.dark:text-brand-400]="docStatusMeta[doc.kycStatus].palette === 'brand'"
                        [class.bg-warning-50]="docStatusMeta[doc.kycStatus].palette === 'warning'"
                        [class.text-warning-700]="docStatusMeta[doc.kycStatus].palette === 'warning'"
                        [class.dark:bg-warning-500/15]="docStatusMeta[doc.kycStatus].palette === 'warning'"
                        [class.dark:text-orange-400]="docStatusMeta[doc.kycStatus].palette === 'warning'"
                        [class.bg-error-50]="docStatusMeta[doc.kycStatus].palette === 'error'"
                        [class.text-error-700]="docStatusMeta[doc.kycStatus].palette === 'error'"
                        [class.dark:bg-error-500/15]="docStatusMeta[doc.kycStatus].palette === 'error'"
                        [class.dark:text-error-400]="docStatusMeta[doc.kycStatus].palette === 'error'"
                        [class.bg-gray-100]="docStatusMeta[doc.kycStatus].palette === 'gray'"
                        [class.text-gray-700]="docStatusMeta[doc.kycStatus].palette === 'gray'"
                        [class.dark:bg-gray-800]="docStatusMeta[doc.kycStatus].palette === 'gray'"
                        [class.dark:text-gray-300]="docStatusMeta[doc.kycStatus].palette === 'gray'"
                      >{{ docStatusMeta[doc.kycStatus].label }}</span>
                    </td>
                    <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {{ doc.issueDate ? (doc.issueDate | ldate:'dd/MM/yyyy') : '—' }}
                    </td>
                    <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {{ doc.expiryDate ? (doc.expiryDate | ldate:'dd/MM/yyyy') : '—' }}
                    </td>
                    <td class="whitespace-nowrap px-6 py-4 text-right">
                      <div class="inline-flex items-center gap-1.5">
                        @if (doc.kycStatus !== 'REJECTED') {
                          <button type="button" (click)="openDocReject(doc)" [disabled]="kycActingId() !== null"
                            class="rounded-md px-2.5 py-1 text-xs font-medium text-error-600 transition hover:bg-error-50 disabled:opacity-40 dark:text-error-400 dark:hover:bg-error-500/10">
                            Rejeter
                          </button>
                        }
                        @if (doc.kycStatus !== 'VALIDATED') {
                          <button type="button" (click)="validateDoc(doc)" [disabled]="kycActingId() !== null"
                            class="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
                            @if (kycActingId() === doc.id) {
                              <svg class="size-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
                              Validation…
                            } @else { Valider }
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    }

    <!-- Metadata -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Métadonnées</h2>
      <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">ID interne</dt>
          <dd class="text-right font-mono text-gray-800 dark:text-white/90">#{{ o.id }}</dd>
        </div>
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">Créé le</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ o.createdAt | ldate:'dd/MM/yyyy HH:mm' }}</dd>
        </div>
        @if (o.updatedAt) {
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Mise à jour</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ o.updatedAt | ldate:'dd/MM/yyyy HH:mm' }}</dd>
          </div>
        }
      </dl>
    </section>
  }
</div>

<!-- Approve modal -->
<app-modal [isOpen]="approveOpen()" (close)="closeApprove()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Approuver l'opérateur</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    L'opérateur passera en <strong>ACTIVE</strong> et pourra percevoir des commissions. Tu peux fixer dès maintenant un palier de licence et des taux override (laisse vide pour les valeurs globales).
  </p>

  @if (approveError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ approveError() }}
    </div>
  }

  <form [formGroup]="approveForm" (ngSubmit)="submitApprove()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Palier de licence</label>
      <app-pretty-select formControlName="licenseTier" [options]="tierOptions" />
    </div>
    <div class="grid grid-cols-3 gap-3">
      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Total %</label>
        <input type="number" step="0.01" formControlName="customTotalPct" placeholder="défaut"
          class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      </div>
      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Plateforme %</label>
        <input type="number" step="0.01" formControlName="customPlatformPct" placeholder="défaut"
          class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      </div>
      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Opérateur %</label>
        <input type="number" step="0.01" formControlName="customOperatorPct" placeholder="défaut"
          class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      </div>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeApprove()" [disabled]="approving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="approving() || approveForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (approving()) { Approbation… } @else { Approuver }
      </button>
    </div>
  </form>
</app-modal>

<!-- Credit reference modal -->
<app-modal [isOpen]="creditOpen()" (close)="closeCredit()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Référence CREDIT individuelle</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Montant de référence utilisé pour les paliers 30 / 10 / 0 % côté wallet. Laisse vide pour retomber sur la référence globale.
  </p>

  @if (creditError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ creditError() }}
    </div>
  }

  <form [formGroup]="creditForm" (ngSubmit)="submitCredit()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Montant de référence (XOF)</label>
      <input type="number" step="1" min="0" formControlName="amount" placeholder="ex. 50000 — vide = défaut global"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Raison * (audit)</label>
      <textarea rows="3" formControlName="reason" placeholder="Ex. : volume médian zone × 5"
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeCredit()" [disabled]="creditSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="creditSaving() || creditForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (creditSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>

<!-- Commission override modal -->
<app-modal [isOpen]="commissionOpen()" (close)="closeCommission()" className="max-w-lg p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Taux de commission</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Choisis d'appliquer les taux globaux de la plateforme ou de définir un override spécifique pour cet opérateur.
  </p>

  @if (commissionError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ commissionError() }}
    </div>
  }

  <form [formGroup]="commissionForm" (ngSubmit)="submitCommission()" class="mt-5 space-y-5">
    <!-- Mode switch -->
    <div class="space-y-2">
      <label class="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition"
             [class.border-brand-500]="commissionForm.controls.useGlobal.value"
             [class.bg-brand-50]="commissionForm.controls.useGlobal.value"
             [class.dark:bg-brand-500/10]="commissionForm.controls.useGlobal.value"
             [class.border-gray-200]="!commissionForm.controls.useGlobal.value"
             [class.dark:border-gray-700]="!commissionForm.controls.useGlobal.value">
        <input type="radio" formControlName="useGlobal" [value]="true" class="mt-0.5 h-4 w-4 accent-brand-500" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-gray-800 dark:text-white/90">Utiliser les taux globaux</p>
          @if (globalConfig(); as g) {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Total {{ g.defaultTotalPct }} % — plateforme {{ g.defaultPlatformPct }} %, opérateur {{ g.defaultOperatorPct }} %
            </p>
          } @else {
            <p class="mt-1 text-xs text-gray-400">Chargement des défauts…</p>
          }
        </div>
      </label>
      <label class="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition"
             [class.border-brand-500]="!commissionForm.controls.useGlobal.value"
             [class.bg-brand-50]="!commissionForm.controls.useGlobal.value"
             [class.dark:bg-brand-500/10]="!commissionForm.controls.useGlobal.value"
             [class.border-gray-200]="commissionForm.controls.useGlobal.value"
             [class.dark:border-gray-700]="commissionForm.controls.useGlobal.value">
        <input type="radio" formControlName="useGlobal" [value]="false" class="mt-0.5 h-4 w-4 accent-brand-500" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-gray-800 dark:text-white/90">Définir un override personnalisé</p>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Saisir des taux spécifiques. Total = plateforme + opérateur.
          </p>
        </div>
      </label>
    </div>

    <!-- Custom rates (only when useGlobal=false) -->
    @if (!commissionForm.controls.useGlobal.value) {
      <div class="grid grid-cols-3 gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Total %</label>
          <input type="number" step="0.01" min="0" max="100" formControlName="customTotalPct"
            class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Plateforme %</label>
          <input type="number" step="0.01" min="0" max="100" formControlName="customPlatformPct"
            class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Opérateur %</label>
          <input type="number" step="0.01" min="0" max="100" formControlName="customOperatorPct"
            class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>
        <p class="col-span-3 text-[11px] text-gray-500 dark:text-gray-400">
          Aperçu somme : plat. + opé. =
          <strong>{{ commissionForm.controls.customPlatformPct.value + commissionForm.controls.customOperatorPct.value }} %</strong>
        </p>
      </div>
    }

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeCommission()" [disabled]="commissionSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="commissionSaving()" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (commissionSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>

<!-- Modal : rejeter un justificatif KYC -->
<app-modal [isOpen]="docRejectOpen()" (close)="closeDocReject()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Rejeter le justificatif</h3>
  @if (docRejectTarget(); as t) {
    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
      Type : <strong>{{ docTypeLabel[t.documentType] }}</strong>. Le motif s'affichera sur ce
      document dans l'espace KYC de l'opérateur.
    </p>
  }

  @if (docRejectError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ docRejectError() }}
    </div>
  }

  <form [formGroup]="docRejectForm" (ngSubmit)="submitDocReject()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Motif <span class="text-error-500">*</span></label>
      <textarea formControlName="reason" rows="3" maxlength="500" placeholder="Ex. : document illisible, expiration trop courte…"
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
      @if (docRejectForm.controls.reason.invalid && docRejectForm.controls.reason.touched) {
        <p class="mt-1 text-xs text-error-600">Motif requis (3 caractères minimum).</p>
      }
    </div>
    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeDocReject()" [disabled]="docRejecting()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="docRejecting() || docRejectForm.invalid" class="inline-flex items-center gap-2 rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-error-700 disabled:cursor-not-allowed disabled:opacity-50">
        @if (docRejecting()) {
          <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
          Rejet…
        } @else { Rejeter le justificatif }
      </button>
    </div>
  </form>
</app-modal>

<!-- Modal : rejeter le dossier KYC (décision, notifie l'opérateur) -->
<app-modal [isOpen]="dossierRejectOpen()" (close)="closeDossierReject()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Rejeter le dossier KYC</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    L'opérateur sera notifié du motif et pourra corriger puis soumettre à nouveau. Il ne
    pourra pas recruter tant que son dossier n'est pas validé.
  </p>

  @if (dossierRejectError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ dossierRejectError() }}
    </div>
  }

  <form [formGroup]="dossierRejectForm" (ngSubmit)="submitDossierReject()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Motif <span class="text-error-500">*</span></label>
      <textarea formControlName="reason" rows="3" maxlength="500" placeholder="Ex. : registre de commerce illisible."
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
      @if (dossierRejectForm.controls.reason.invalid && dossierRejectForm.controls.reason.touched) {
        <p class="mt-1 text-xs text-error-600">Motif requis (3 caractères minimum).</p>
      }
    </div>
    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeDossierReject()" [disabled]="dossierRejecting()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="dossierRejecting() || dossierRejectForm.invalid" class="inline-flex items-center gap-2 rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-error-700 disabled:cursor-not-allowed disabled:opacity-50">
        @if (dossierRejecting()) {
          <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path></svg>
          Rejet…
        } @else { Rejeter le dossier }
      </button>
    </div>
  </form>
</app-modal>

<!-- Lightbox d'inspection des justificatifs -->
<app-image-lightbox [src]="lightboxSrc()" (close)="lightboxSrc.set(null)" />
